/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { Alert, Badge, Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { Button } from "../../Helper/Button";
import { useSignalDetail } from "./signalHooks";
import { isClosed } from "./signalFiltering";
import {
    CATEGORY_COLOR,
    CATEGORY_LABEL,
    getCellRelation,
    getPalletRelations,
    getRecommendedAction,
    getSignalCategory,
    Signal,
    STATUS_LABEL,
} from "./signalTypes";

// Attribute keys that are rendered elsewhere (header/banner/category) and so
// are excluded from the generic context grid.
const HIDDEN_ATTRS = new Set(["category", "recommended_action", "cell_label"]);

interface SignalDetailModalProps {
    signal: Signal | null;
    isOpen: boolean;
    onClose: () => void;
    onResolveReject: (signalId: string, status: "resolved" | "rejected", note?: string) => Promise<void> | void;
    isSubmitting?: boolean;
}

const SignalDetailModal: React.FC<SignalDetailModalProps> = ({ signal, isOpen, onClose, onResolveReject, isSubmitting }) => {
    // The detail fetch adds the audit timeline (events) the list query omits.
    const { data: detail } = useSignalDetail(signal?.id);
    const full = detail || signal;

    const [note, setNote] = useState("");
    useEffect(() => setNote(""), [signal?.id]);

    if (!signal || !full) return null;

    const category = getSignalCategory(full);
    const recommendedAction = getRecommendedAction(full);
    const closed = isClosed(full);
    const cell = getCellRelation(full);
    const pallets = getPalletRelations(full);
    const events = full.events || [];
    const contextEntries = Object.entries(full.attributes || {}).filter(([key]) => !HIDDEN_ATTRS.has(key));

    const act = (status: "resolved" | "rejected") => onResolveReject(full.id, status, note.trim() || undefined);

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="md" centered>
            <ModalHeader toggle={onClose} className="fs-5">
                <span className="d-flex align-items-center gap-2">
                    {category && <Badge color={CATEGORY_COLOR[category]}>{CATEGORY_LABEL[category]}</Badge>}
                    {full.title}
                </span>
            </ModalHeader>

            <ModalBody>
                {/* Recommended-action banner */}
                {recommendedAction && (
                    <Alert color={category ? CATEGORY_COLOR[category] : "secondary"} className="py-2">
                        <strong>Recommended:</strong> {recommendedAction}
                    </Alert>
                )}

                {/* Cell + (Cat 3) pallet links */}
                <div className="mb-3 small text-muted d-flex flex-wrap gap-2 align-items-center">
                    {/* Labels are what an operator recognises; the id stays reachable on hover. */}
                    {cell && (
                        <span>Cell: <strong title={cell.facility_location_id}>{cell.facility_location_label || cell.facility_location_id}</strong></span>
                    )}
                    {pallets.map((p) => (
                        <Badge key={p.id} color="light" className="text-dark border" title={p.layer_id}>
                            {p.layer_label || p.layer_id}
                        </Badge>
                    ))}
                    <Badge color="secondary" className="ms-auto">{STATUS_LABEL[full.status] || full.status}</Badge>
                </div>

                {/* Category-aware context (from attributes) */}
                {contextEntries.length > 0 && (
                    <dl className="row mb-3 small">
                        {contextEntries.map(([key, value]) => (
                            <React.Fragment key={key}>
                                <dt className="col-5 text-muted text-truncate">{key.replace(/_/g, " ")}</dt>
                                <dd className="col-7 mb-1">{String(value)}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                )}

                {full.description && <p className="mb-3">{full.description}</p>}

                {/* Activity timeline */}
                <div className="mb-3">
                    <div className="fw-bold small text-muted mb-2">Activity</div>
                    {events.length === 0 ? (
                        <div className="small text-muted">No activity yet.</div>
                    ) : (
                        <ul className="list-unstyled mb-0" css={css`border-left: 2px solid #dee2e6; padding-left: 12px;`}>
                            {events.map((event) => (
                                <li key={event.id} className="mb-2 small">
                                    <span className="fw-bold text-capitalize">{event.event}</span>{" "}
                                    <span className="text-muted">by {event.actor}</span>
                                    <div className="text-muted">{new Date(event.created).toLocaleString()}</div>
                                    {event.note && <div className="fst-italic">“{event.note}”</div>}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Inline optional note — only when actionable (open) */}
                {!closed && (
                    <Input
                        type="textarea"
                        rows={2}
                        placeholder="Optional note (attached to resolve / reject)…"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                )}
            </ModalBody>

            <ModalFooter className="d-flex justify-content-between gap-2">
                {closed ? (
                    <span className="text-muted small">Closed — read-only.</span>
                ) : (
                    <div className="d-flex gap-2">
                        <Button color="danger" outline onClick={() => act("rejected")} isLoading={isSubmitting}>Reject</Button>
                        <Button color="success" onClick={() => act("resolved")} isLoading={isSubmitting}>Resolve</Button>
                    </div>
                )}
                <Button color="secondary" outline onClick={onClose}>Close</Button>
            </ModalFooter>
        </Modal>
    );
};

export default SignalDetailModal;
